import { Autocomplete, CircularProgress, InputAdornment, TextField } from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Search } from 'lucide-react';
import type { ReactNode, SyntheticEvent } from 'react';
import type { FileUploadLibraryOption } from './types';
import * as S from './FileUpload.styles';

export interface FileUploadLibrarySearchProps<
  T extends FileUploadLibraryOption = FileUploadLibraryOption,
> {
  options: T[];
  loading?: boolean;
  value: T | T[] | null;
  onChange: (event: SyntheticEvent, value: T | T[] | null) => void;
  multiple?: boolean;
  disableCloseOnSelect?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Shown when the menu is open and the options list is empty. */
  noOptionsText?: string;
  /**
   * Rows the picker SHOWS AND REFUSES — offered, listed, never selectable.
   *
   * A row the caller cannot accept is a fact the auditor needs (the file is in the
   * library, it just cannot be imported); dropping it from the options instead leaves
   * them scrolling for a document that is right there.
   */
  getOptionDisabled?: (option: T) => boolean;
  /**
   * Heading over a run of rows. Return `''` for the rows that get no heading —
   * the ungrouped half of a list that is only partly grouped.
   *
   * MUI groups by ADJACENCY, so `options` must already be ordered by group or the
   * same heading renders more than once.
   */
  groupBy?: (option: T) => string;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: string },
    option: T,
  ) => ReactNode;
}

/**
 * White search field for picking library documents. Pure UI — no data hooks.
 */
export function FileUploadLibrarySearch<
  T extends FileUploadLibraryOption = FileUploadLibraryOption,
>({
  options,
  loading = false,
  value,
  onChange,
  multiple = false,
  disableCloseOnSelect = multiple,
  placeholder = '',
  disabled = false,
  noOptionsText,
  getOptionDisabled,
  groupBy,
  renderOption,
}: FileUploadLibrarySearchProps<T>) {
  return (
    <S.FileUploadLibraryField data-testid='file-upload-library-search'>
      <Autocomplete<T, boolean, boolean, false>
        multiple={multiple}
        fullWidth
        clearIcon={null}
        forcePopupIcon={false}
        loading={loading}
        options={options}
        value={value}
        onChange={onChange}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, v) => option.id === v.id}
        disableCloseOnSelect={disableCloseOnSelect}
        disabled={disabled}
        noOptionsText={noOptionsText}
        getOptionDisabled={getOptionDisabled}
        groupBy={groupBy}
        renderGroup={
          groupBy
            ? (params) => (
                /* MUI's own `renderGroup` always draws the label, which would put an
                   empty 32px band above the rows that are simply fine. A heading is
                   only earned by a group that has something to say. */
                <li key={params.key}>
                  {params.group ? (
                    <S.FileUploadLibraryGroupLabel>{params.group}</S.FileUploadLibraryGroupLabel>
                  ) : null}
                  <S.FileUploadLibraryGroupList>{params.children}</S.FileUploadLibraryGroupList>
                </li>
              )
            : undefined
        }
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            placeholder={placeholder}
            size='small'
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position='end'>
                      <Search size={18} strokeWidth={2} aria-hidden />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color='inherit' size={14} sx={{ mr: 0.5 }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={
          renderOption ??
          ((props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          ))
        }
      />
    </S.FileUploadLibraryField>
  );
}
